import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import api from '../api';

const Leaderboard = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState('current');
  const [players, setPlayers] = useState([]);
  const [previousPlayers, setPreviousPlayers] = useState([]);
  const [currentRace, setCurrentRace] = useState(null);
  const [prizes, setPrizes] = useState({});
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leaderboardData, previousData, raceData, prizesData] = await Promise.all([
        api.getLeaderboard(),
        api.getPreviousLeaderboard(),
        api.getCurrentRace(),
        api.getPrizes()
      ]);
      
      setPlayers(Array.isArray(leaderboardData) ? leaderboardData : []);
      setPreviousPlayers(Array.isArray(previousData) ? previousData : []);
      setCurrentRace(raceData);
      
      if (Array.isArray(prizesData)) {
        const prizeMap = {};
        prizesData.forEach(p => prizeMap[p.rank] = p.amount);
        setPrizes(prizeMap);
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentRace?.end_date) return;
    
    const calculateTime = () => {
      try {
        const endDateStr = currentRace.end_date.split('T')[0];
        const end = new Date(endDateStr + 'T23:59:59');
        const now = new Date();
        const diff = end - now;
        
        if (diff <= 0 || isNaN(diff)) {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          return;
        }
        
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / (1000 * 60)) % 60),
          seconds: Math.floor((diff / 1000) % 60)
        });
      } catch (err) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    
    calculateTime();
    const timer = setInterval(calculateTime, 1000);
    return () => clearInterval(timer);
  }, [currentRace]);

  const displayPlayers = tab === 'current' ? players : previousPlayers;
  const prizePool = currentRace?.prize_pool || 7500;

  const fmt = (n) => '$' + Number(n || 0).toLocaleString();
  const fmtWager = (n) => '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Remplir jusqu'à 15 positions
  const fullLeaderboard = [...Array(15)].map((_, i) => {
    const rank = i + 1;
    const player = displayPlayers[i];
    return {
      rank,
      username: player?.username || '—',
      wagered: player?.wagered || 0,
      prize: rank <= 10 ? (prizes[rank] || 0) : 0,
      isEmpty: !player
    };
  });

  const podium = fullLeaderboard.slice(0, 3);
  const table = fullLeaderboard.slice(3);

  return (
    <div className="leaderboard">
      <p className="lb-title">{t('raceBiWeekly')}</p>
      <h2 className="lb-prize">{fmt(prizePool)}</h2>
      
      {currentRace && (
        <div className="timer-container">
          <div className="timer">
            <div className="timer-block">
              <span className="timer-value">{String(timeLeft.days || 0).padStart(2, '0')}</span>
              <span className="timer-unit">{t('days')}</span>
            </div>
            <span className="timer-sep">:</span>
            <div className="timer-block">
              <span className="timer-value">{String(timeLeft.hours || 0).padStart(2, '0')}</span>
              <span className="timer-unit">{t('hours')}</span>
            </div>
            <span className="timer-sep">:</span>
            <div className="timer-block">
              <span className="timer-value">{String(timeLeft.minutes || 0).padStart(2, '0')}</span>
              <span className="timer-unit">{t('minutes')}</span>
            </div>
            <span className="timer-sep">:</span>
            <div className="timer-block">
              <span className="timer-value">{String(timeLeft.seconds || 0).padStart(2, '0')}</span>
              <span className="timer-unit">{t('seconds')}</span>
            </div>
          </div>
        </div>
      )}

      <div className="lb-tabs">
        <button className={`lb-tab ${tab === 'current' ? 'active' : ''}`} onClick={() => setTab('current')}>
          {t('current')}
        </button>
        <button className={`lb-tab ${tab === 'previous' ? 'active' : ''}`} onClick={() => setTab('previous')}>
          {t('previous')}
        </button>
      </div>

      {loading ? (
        <p style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>{t('loading')}</p>
      ) : (
        <>
          {/* Podium - Ordre: 2ème, 1er, 3ème (1er au milieu) */}
          <div className="podium-container">
            {/* 2ème place */}
            <div className="podium-card second">
              <div className="podium-box">
                <p className="podium-name">
                  <span className="medal">🥈</span>
                  {podium[1].username}
                </p>
                <p className="podium-wagered">{fmtWager(podium[1].wagered)}</p>
                <p className="podium-label">{t('wagered')}</p>
                <div className="podium-prize podium-prize-silver">
                  {fmt(podium[1].prize)}
                </div>
              </div>
            </div>
            
            {/* 1ère place (au milieu) */}
            <div className="podium-card first">
              <div className="podium-box">
                <p className="podium-name">
                  <span className="medal">🥇</span>
                  {podium[0].username}
                </p>
                <p className="podium-wagered">{fmtWager(podium[0].wagered)}</p>
                <p className="podium-label">{t('wagered')}</p>
                <div className="podium-prize podium-prize-gold">
                  {fmt(podium[0].prize)}
                </div>
              </div>
            </div>
            
            {/* 3ème place */}
            <div className="podium-card third">
              <div className="podium-box">
                <p className="podium-name">
                  <span className="medal">🥉</span>
                  {podium[2].username}
                </p>
                <p className="podium-wagered">{fmtWager(podium[2].wagered)}</p>
                <p className="podium-label">{t('wagered')}</p>
                <div className="podium-prize podium-prize-bronze">
                  {fmt(podium[2].prize)}
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="lb-table">
            <div className="lb-header">
              <span>{t('rank')}</span>
              <span>{t('player')}</span>
              <span style={{ textAlign: 'right' }}>{t('wagered')}</span>
              <span style={{ textAlign: 'right' }}>{t('prize')}</span>
            </div>
            {table.map((p, i) => {
              const isUnpaid = p.rank > 10;
              return (
                <React.Fragment key={i}>
                  {p.rank === 11 && (
                    <div className="lb-separator">— — — {t('notPaid')} — — —</div>
                  )}
                  <div className={`lb-row ${isUnpaid ? 'unpaid' : ''}`}>
                    <span className="lb-rank">#{p.rank}</span>
                    <span className="lb-player">{p.username}</span>
                    <span className="lb-wager">{p.isEmpty ? '—' : fmtWager(p.wagered)}</span>
                    <span className="lb-prize-cell">{isUnpaid ? '—' : fmt(p.prize)}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Leaderboard;
